<!--
  StatusSelectField — renderer for icon+label status fields (task_status,
  review_status, deal_status, …): [icon] label [chevron].

  Editing (mode === "editing"): a small option list of all states, rendered
  INSIDE the cell's own overlay (GridCell teleports it; CardCell edits in place),
  never a body-teleported popover — a teleported menu reads as an outside-press
  and tears down the cell's edit lifecycle before the pick commits (same reason
  SelectValueField renders its dropdown this way).

  Clicking the leading icon advances the status to the next value in the type's
  sequence (its own values() by default; see STATUS_SEQUENCES for overrides, e.g.
  task_status cycles Todo↔Done, deal_status Open↔Won). Read-only fields show a plain, static icon.

  Chrome-free like the other XXXField renderers: the host cell owns the
  hover/selection frame, padding, and edit affordance. Emits the status value
  only; any side effects (e.g. task completed_at) live in the domain flows.
-->
<template>
  <!-- Editing: the option list, hosted by the cell overlay where @commit is wired. -->
  <div
    v-if="editable && mode === 'editing'"
    ref="menuRef"
    class="status-select-menu"
    :class="sizeClass"
    tabindex="-1"
    @keydown="onKeydown"
    @mousedown.prevent
  >
    <button
      v-for="(opt, i) in options"
      :key="opt.value"
      type="button"
      class="ss-option"
      :class="{ 'is-selected': String(opt.value) === status, 'is-highlighted': i === highlighted }"
      @click.stop="pick(opt.value)"
      @mouseenter="highlighted = i"
    >
      <StatusIcon :status="String(opt.value)" :size="iconSize" />
      <span>{{ opt.label }}</span>
    </button>
  </div>

  <!-- Display: icon + label + chevron. -->
  <div v-else class="status-select-field" :class="sizeClass">
    <div class="status-select-button">
      <button
        v-if="editable"
        type="button"
        class="ss-icon-btn"
        :title="nextTitle"
        :aria-label="nextTitle"
        @click="advance"
      >
        <StatusIcon :status="status" :size="iconSize" />
      </button>
      <span v-else class="ss-icon-btn is-static">
        <StatusIcon :status="status" :size="iconSize" />
      </span>

      <span class="ss-label" :class="{ 'is-static': !editable }">{{ currentLabel }}</span>
      <font-awesome-icon v-if="editable" class="ss-chev" icon="chevron-down" size="xs" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { type Field, getFieldType, selectOptions } from "@auger/core/fields"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"
import StatusIcon from "./StatusIcon.vue"

// The sequence the leading icon advances through on click. Defaults to the
// field's own values(); a type listed here overrides that order (task_status
// only cycles Todo↔Done, skipping doing/dismissed). UI-only, so it lives here
// rather than on the (data-only) type registry.
const STATUS_SEQUENCES: Record<string, readonly string[]> = {
  task_status: ["todo", "done"],
  deal_status: ["open", "won"],
}

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode?: FieldMode
    size?: FieldSize
  }>(),
  { size: "normal", mode: "inline" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const sizeClass = computed(() => textSizeClass(props.size))

const ICON_SIZES: Record<FieldSize, number> = { small: 16, normal: 18, medium: 22, large: 26 }
const iconSize = computed(() => ICON_SIZES[props.size])

const editable = computed(() => !!props.field.isEditable)

// Options from the field's own `values`, falling back to the registered type's
// baked values (schema fields carry them; ad-hoc columns may not).
const options = computed(() => {
  const typeDef = getFieldType(props.field.type) as { values?: readonly unknown[] } | undefined
  return selectOptions({ values: props.field.values ?? typeDef?.values })
})

const status = computed(() => {
  const v = props.modelValue
  return v == null || v === "" ? String(options.value[0]?.value ?? "") : String(v)
})
const currentLabel = computed(
  () => options.value.find((o) => String(o.value) === status.value)?.label ?? status.value
)

// The value clicking the icon advances to: the next entry in this type's
// sequence (its own values() unless overridden), wrapping. Undefined when
// read-only or the sequence is empty.
const nextValue = computed(() => {
  if (!editable.value) return undefined
  const seq = STATUS_SEQUENCES[props.field.type] ?? options.value.map((o) => String(o.value))
  if (!seq.length) return undefined
  return seq[(seq.indexOf(status.value) + 1) % seq.length]
})

const nextTitle = computed(() => {
  if (nextValue.value == null) return undefined
  const label =
    options.value.find((o) => String(o.value) === nextValue.value)?.label ?? nextValue.value
  return `Set to ${label}`
})

// Advance the status in place. Inline, the click must bubble so the host
// activates the cell (its overlay is where @commit is wired); in the active
// overlay we stop it so it doesn't also trip the cell's click-to-edit.
function advance(event: MouseEvent) {
  if (nextValue.value == null) return
  if (props.mode !== "inline") event.stopPropagation()
  emit("update:modelValue", nextValue.value)
  emit("commit")
}
function pick(value: string | number) {
  emit("update:modelValue", String(value))
  emit("commit")
}

// Editing (option list): focus the list and highlight the current value so
// arrow-keys + Enter work, mirroring SelectValueField.
const menuRef = ref<HTMLElement | null>(null)
const highlighted = ref(0)
watch(
  () => props.mode === "editing",
  (on) => {
    if (!on) return
    const idx = options.value.findIndex((o) => String(o.value) === status.value)
    highlighted.value = idx >= 0 ? idx : 0
    nextTick(() => menuRef.value?.focus())
  }
)

function onKeydown(event: KeyboardEvent) {
  const opts = options.value
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault()
    event.stopPropagation()
    const opt = opts[highlighted.value]
    if (opt) pick(opt.value)
  } else if (event.key === "ArrowDown") {
    event.preventDefault()
    event.stopPropagation()
    highlighted.value = Math.min(highlighted.value + 1, opts.length - 1)
  } else if (event.key === "ArrowUp") {
    event.preventDefault()
    event.stopPropagation()
    highlighted.value = Math.max(highlighted.value - 1, 0)
  }
}
</script>

<style>
.status-select-field {
  /* The field root fills the cell, so the container query below reflects the
     column width and can drop the label when the column gets narrow. */
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 16px;
  container-type: inline-size;

  /* Chrome-free: the host cell (GridCell / CardCell) owns hover, selection
     frame, padding, and the edit affordance. This is just the interactive
     content, filling the field so the compact query can center it. */
  .status-select-button {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 5px;
    /* a hair of breathing room before the icon in icon+text mode */
    padding-left: 4px;
    /* the control isn't text: no I-beam, no text selection (override the cell's
       click-to-edit `cursor: text` hint) */
    cursor: default;
    user-select: none;

    .ss-icon-btn {
      appearance: none;
      background: none;
      border: none;
      padding: 3px;
      margin: -3px;
      line-height: 0;
      border-radius: 50%;
      cursor: pointer;
      transition: transform 0.12s ease;

      &.is-static {
        cursor: default;
      }
      &:hover:not(.is-static) {
        transform: scale(1.05);
      }
      &:active:not(.is-static) {
        transform: scale(0.95);
      }
      &:focus-visible {
        outline: 2px solid var(--auger-info);
        outline-offset: 1px;
      }
    }

    .ss-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.is-static {
        color: var(--auger-text);
      }
    }

    /* Chevron is a hover affordance (mirrors the select field): hidden at rest,
       revealed on cell hover, and always shown while the cell is active/editing.
       opacity (not display) keeps layout stable so nothing shifts on hover. */
    .ss-chev {
      color: var(--auger-text-weak);
      opacity: 0;
      transition: opacity 0.12s ease;

      /* stays visible while the field sits in the grid's edit overlay */
      .grid-editor & {
        opacity: 1;
      }
    }
  }

  &:hover .ss-chev {
    opacity: 1;
  }

  /* Not enough room for the label → just the icon, centered. The chevron is
     pulled out of flow so alignment ignores it (the icon sits dead-center). */
  @container (max-width: 132px) {
    .ss-label:not(.is-static) {
      display: none;
    }
    .status-select-button {
      justify-content: center;
      position: relative;
      padding-left: 0;

      .ss-chev {
        position: absolute;
        top: 50%;
        left: 50%;
        /* a hair more gap between the icon and the (revealed) chevron */
        margin: -6px 0 0 13px;
      }
    }
  }

  /* Too narrow for even the icon + chevron → drop the chevron so the icon alone
     stays centered (e.g. a bare status control in a content-sized track). */
  @container (max-width: 22px) {
    .ss-chev {
      display: none;
    }
  }
}

/* Editing: the option list in the cell overlay. */
.status-select-menu {
  display: block;
  width: 100%;
  outline: none;

  .ss-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 4px 6px;
    border: none;
    border-radius: var(--auger-radius);
    background: none;
    color: var(--auger-text);
    font: inherit;
    text-align: left;
    cursor: pointer;

    &.is-selected {
      font-weight: 600;
    }
    &.is-highlighted {
      background: var(--auger-bg-muted);
    }
  }
}
</style>
